import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  HelpCircle, 
  Target, 
  Play, 
  Zap, 
  Shield, 
  BarChart2,
  Keyboard,
  MousePointer,
  AlertTriangle,
  CheckCircle,
  Info,
  Lightbulb,
  Star
} from 'lucide-react';

interface ScoringTip {
  id: string;
  title: string;
  description: string;
  category: 'info' | 'tip' | 'warning' | 'success';
  icon: React.ReactNode;
}

interface ScoringCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  tips: ScoringTip[];
}

const ScoringGuide: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basics');

  const scoringCategories: ScoringCategory[] = [
    {
      id: 'basics',
      title: 'Basic Scoring',
      description: 'Learn the fundamentals of ball-by-ball scoring',
      icon: <Target className="h-5 w-5" />,
      tips: [
        {
          id: 'runs-scoring',
          title: 'Recording Runs',
          description: 'Click the run buttons (0-6) to record runs scored off each ball. The system automatically updates the score and ball count.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'wicket-scoring',
          title: 'Recording Wickets',
          description: 'Click the wicket button (W) when a batsman is dismissed. You\'ll be prompted to select the dismissal type and involved players.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'dot-balls',
          title: 'Dot Balls',
          description: 'Click the "0" button for dot balls (no runs scored). This is important for accurate bowling statistics.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'boundaries',
          title: 'Boundaries',
          description: 'When recording 4 or 6 runs, the system automatically detects boundaries and updates boundary statistics.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        }
      ]
    },
    {
      id: 'extras',
      title: 'Extras & Free Hits',
      description: 'Handle wides, no-balls, byes, and leg-byes',
      icon: <Zap className="h-5 w-5" />,
      tips: [
        {
          id: 'wides',
          title: 'Wides',
          description: 'Click the "Wide" button when the bowler bowls a wide. You can add extra runs if the batsmen run between wickets.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'no-balls',
          title: 'No-Balls',
          description: 'Click the "No-Ball" button for illegal deliveries. The next ball becomes a free hit automatically.',
          category: 'warning',
          icon: <AlertTriangle className="h-4 w-4" />
        },
        {
          id: 'free-hits',
          title: 'Free Hits',
          description: 'After a no-ball, the next ball is a free hit. The "FH" indicator will show on the ball button.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'byes-legbyes',
          title: 'Byes & Leg Byes',
          description: 'Use "Byes" when the ball passes the batsman without contact, and "Leg Byes" when it hits the batsman\'s body.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        }
      ]
    },
    {
      id: 'overs',
      title: 'Over Management',
      description: 'Manage overs, power plays, and extra balls',
      icon: <Shield className="h-5 w-5" />,
      tips: [
        {
          id: 'over-completion',
          title: 'Over Completion',
          description: 'The system automatically tracks balls per over. When 6 legal balls are bowled, the over is complete.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'extra-balls',
          title: 'Extra Balls',
          description: 'Extra balls from wides and no-balls are tracked separately and don\'t count towards the 6-ball over.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'power-plays',
          title: 'Power Plays',
          description: 'Configure power play overs in match settings. Fielding restrictions are automatically applied.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'over-rate',
          title: 'Over Rate',
          description: 'Monitor the over rate to ensure matches stay on schedule. The system tracks time between overs.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        }
      ]
    },
    {
      id: 'statistics',
      title: 'Live Statistics',
      description: 'Track player and team performance in real-time',
      icon: <BarChart2 className="h-5 w-5" />,
      tips: [
        {
          id: 'batting-stats',
          title: 'Batting Statistics',
          description: 'Runs, boundaries, strike rate, and dismissal details are automatically calculated for each batsman.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'bowling-stats',
          title: 'Bowling Statistics',
          description: 'Overs, maidens, wickets, economy rate, and extras are tracked for each bowler.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'partnerships',
          title: 'Partnerships',
          description: 'Current partnership runs and balls are displayed in real-time.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'required-rate',
          title: 'Required Run Rate',
          description: 'For chase scenarios, the required run rate is calculated and displayed automatically.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        }
      ]
    },
    {
      id: 'shortcuts',
      title: 'Keyboard Shortcuts',
      description: 'Speed up scoring with keyboard shortcuts',
      icon: <Keyboard className="h-5 w-5" />,
      tips: [
        {
          id: 'run-keys',
          title: 'Run Keys (1-6)',
          description: 'Press keys 1-6 to quickly record runs without using the mouse.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'wicket-key',
          title: 'Wicket Key (W)',
          description: 'Press "W" to quickly record a wicket dismissal.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'zero-key',
          title: 'Dot Ball (0)',
          description: 'Press "0" to record a dot ball quickly.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'undo-function',
          title: 'Undo Last Ball',
          description: 'Use Ctrl+Z to undo the last ball entry if you made an error.',
          category: 'warning',
          icon: <AlertTriangle className="h-4 w-4" />
        }
      ]
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      description: 'Tips for accurate and efficient scoring',
      icon: <Star className="h-5 w-5" />,
      tips: [
        {
          id: 'double-check',
          title: 'Double-Check Entries',
          description: 'Always double-check your entries, especially for wickets and extras, before confirming.',
          category: 'warning',
          icon: <AlertTriangle className="h-4 w-4" />
        },
        {
          id: 'pause-scoring',
          title: 'Pause When Needed',
          description: 'Use the pause function during interruptions, injuries, or when clarification is needed.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        },
        {
          id: 'backup-scorer',
          title: 'Backup Scorer',
          description: 'Have a backup scorer ready in case of technical issues or for verification.',
          category: 'info',
          icon: <Info className="h-4 w-4" />
        },
        {
          id: 'scoreboard-display',
          title: 'Scoreboard Display',
          description: 'Keep the live scoreboard visible to players and spectators for transparency.',
          category: 'tip',
          icon: <Lightbulb className="h-4 w-4" />
        }
      ]
    }
  ];

  const getTipCategoryStyles = (category: string) => {
    switch (category) {
      case 'info':
        return 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'tip':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
      case 'warning':
        return 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 'success':
        return 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/50 dark:text-green-300';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <HelpCircle className="h-4 w-4" />
          Scoring Guide
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Live Scoring Guide
          </DialogTitle>
          <DialogDescription>
            Comprehensive guide for accurate and efficient match scoring
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Quick Reference */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Keyboard className="h-5 w-5" />
                Quick Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">1-6</div>
                  <div className="text-sm text-muted-foreground">Runs</div>
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <div className="text-2xl font-bold text-red-600">W</div>
                  <div className="text-sm text-muted-foreground">Wicket</div>
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">0</div>
                  <div className="text-sm text-muted-foreground">Dot Ball</div>
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">Ctrl+Z</div>
                  <div className="text-sm text-muted-foreground">Undo</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6">
              {scoringCategories.map((category) => (
                <TabsTrigger key={category.id} value={category.id} className="flex items-center gap-2">
                  {category.icon}
                  <span className="hidden md:inline">{category.title}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {scoringCategories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">{category.title}</h3>
                  <p className="text-sm text-muted-foreground">{category.description}</p>
                </div>

                <div className="grid gap-4">
                  {category.tips.map((tip) => (
                    <Card key={tip.id} className={`border ${getTipCategoryStyles(tip.category)}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          {tip.icon}
                          <div>
                            <CardTitle className="text-base">{tip.title}</CardTitle>
                            <CardDescription className="text-sm text-inherit">{tip.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Emergency Contacts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Need Help During Match?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>• Use the pause function if you need time to clarify a decision</p>
                <p>• Double-check all entries before confirming</p>
                <p>• Contact tournament officials for rule clarifications</p>
                <p>• Keep a backup paper scorecard as a precaution</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScoringGuide; 